// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { auth, googleProvider, db, waitForAuthState, isUserAuthenticated } from '../firebase/config';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userCredits, setUserCredits] = useState(0);
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Register new user
  async function signup(email, password, promoCode = null) {
    const result = await createUserWithEmailAndPassword(auth, email, password);

    // Note: User document with 5 credits is automatically created by Firebase Auth trigger
    // promoCode will be applied separately in Register.js after signup
    // No need to manually create user document here

    return result;
  }

  // Login existing user
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Google sign-in
  async function signInWithGoogle() {
    const result = await signInWithPopup(auth, googleProvider);

    // Note: For new users, user document with 5 credits is automatically created by Firebase Auth trigger
    // For existing users, their document already exists
    // No need to manually check/create user document here

    return result;
  }

  // Logout
  function logout() {
    return signOut(auth);
  }

  // Reset password
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // Create user document in Firestore
  async function createUserDocument(user, additionalData = {}) {
    if (!user) return;
    
    const userRef = doc(db, 'users', user.uid);
    const userData = {
      email: user.email,
      displayName: user.displayName || '',
      credits: additionalData.credits || 0,
      totalProcessed: 0,
      createdAt: serverTimestamp(),
      ...additionalData
    };
    
    await setDoc(userRef, userData, { merge: true });
  }

  // Helper function to detect if user is newly registered
  function isNewUser(user) {
    if (!user?.metadata?.creationTime || !user?.metadata?.lastSignInTime) {
      return false;
    }

    const createdAt = new Date(user.metadata.creationTime);
    const lastSignIn = new Date(user.metadata.lastSignInTime);
    const now = new Date();
    const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds

    // BOTH creation and last sign-in must be within 5 minutes
    const isRecentlyCreated = (now - createdAt) <= fiveMinutes;
    const isRecentSignIn = (now - lastSignIn) <= fiveMinutes;

    return isRecentlyCreated && isRecentSignIn;
  }

  // Get user credits with optimistic default for new users
  async function getUserCredits(userId, user = null) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const credits = userDoc.data().credits || 0;
        setUserCredits(credits);
        console.log(`✅ Credits loaded from Firestore: ${credits}`);
        return credits;
      } else {
        // Document doesn't exist - check if user is new
        if (user && isNewUser(user)) {
          console.log(`🆕 New user detected, optimistically showing 5 credits`);
          setUserCredits(5);
          return 5;
        } else {
          console.log(`⚠️ User document not found, defaulting to 0 credits`);
          setUserCredits(0);
          return 0;
        }
      }
    } catch (error) {
      console.error(`❌ Error fetching user credits:`, error);

      // On error, still provide optimistic default for new users
      if (user && isNewUser(user)) {
        console.log(`🆕 Network error but new user detected, optimistically showing 5 credits`);
        setUserCredits(5);
        return 5;
      }

      setUserCredits(0);
      return 0;
    }
  }

  // Update user credits (deduct or add credits)
  async function updateUserCredits(userId, creditChange) {
    try {
      console.log(`💰 AUTH DEBUG: Updating credits for user ${userId} by ${creditChange}`);
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        credits: increment(creditChange),
        lastUpdated: serverTimestamp()
      });
      
      // Refresh credits in state
      await getUserCredits(userId);
      console.log(`✅ AUTH DEBUG: Credits updated successfully`);
      return true;
    } catch (error) {
      console.error('❌ AUTH DEBUG: Failed to update user credits:', error);
      return false;
    }
  }

  // Validate current authentication state
  async function validateAuthState() {
    try {
      await waitForAuthState();
      const isAuth = await isUserAuthenticated();
      console.log('🔐 AUTH VALIDATION: User authenticated:', isAuth);
      return {
        isAuthenticated: isAuth,
        user: auth.currentUser,
        ready: authReady
      };
    } catch (error) {
      console.error('🔐 AUTH VALIDATION ERROR:', error);
      return {
        isAuthenticated: false,
        user: null,
        ready: false,
        error: error.message
      };
    }
  }

  // Force refresh auth token (useful before downloads)
  async function refreshAuthToken() {
    try {
      if (!currentUser) {
        throw new Error('No user logged in');
      }
      
      const token = await currentUser.getIdToken(true); // Force refresh
      console.log('🔐 AUTH TOKEN: Refreshed successfully');
      return token;
    } catch (error) {
      console.error('🔐 AUTH TOKEN ERROR:', error);
      throw new Error('Failed to refresh auth token: ' + error.message);
    }
  }

  useEffect(() => {
    let isMounted = true;
    
    const initializeAuth = async () => {
      try {
        setAuthError(null);
        console.log('🔐 AuthContext: Waiting for auth state...');
        
        // Wait for auth state to be ready from Firebase config
        await waitForAuthState();
        
        if (!isMounted) return;
        
        console.log('🔐 AuthContext: Auth state ready, setting up listener...');
        setAuthReady(true);
        
        // Now set up the auth state listener
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (!isMounted) return;
          
          console.log('🔐 AuthContext: Auth state changed:', user ? 'authenticated' : 'anonymous');
          setCurrentUser(user);
          
          if (user) {
            // Fetch user credits immediately - optimistic defaults handle new users
            try {
              console.log('🔐 AuthContext: Fetching credits for user:', user.uid);
              await getUserCredits(user.uid, user);
              console.log('🔐 AuthContext: Credits fetch completed');
            } catch (error) {
              console.error('Error fetching user credits:', error);
              setAuthError('Failed to load user data');
            }
          } else {
            setUserCredits(0);
          }
          
          setLoading(false);
        }, (error) => {
          console.error('Auth state change error:', error);
          if (isMounted) {
            setAuthError('Authentication error occurred');
            setLoading(false);
          }
        });

        return () => {
          unsubscribe();
        };
        
      } catch (error) {
        console.error('🔐 AuthContext: Auth initialization error:', error);
        if (isMounted) {
          setAuthError('Failed to initialize authentication');
          setLoading(false);
        }
      }
    };

    const cleanup = initializeAuth();
    
    return () => {
      isMounted = false;
      if (cleanup && typeof cleanup.then === 'function') {
        cleanup.then(unsubscribe => {
          if (unsubscribe && typeof unsubscribe === 'function') {
            unsubscribe();
          }
        });
      }
    };
  }, []);


  const value = {
    currentUser,
    userCredits,
    authReady,
    authError,
    loading,
    signup,
    login,
    signInWithGoogle,
    logout,
    resetPassword,
    createUserDocument,
    getUserCredits,
    updateUserCredits,
    validateAuthState,
    refreshAuthToken
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}